import { useAppConstantsContext } from "@/context/AppConstants";
import type { SearchTabData } from "@/reports/index";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

interface SearchFormContextProps {
  useSearchForm: (initialActiveTab?: boolean) => {
    openingSearchForms: boolean;
    isSearchOpen: boolean;
    activeFormTab: string;
    searchTabs: SearchTabData[];
    handleTabChange: (_event: React.SyntheticEvent, newValue: string) => void;
    setActiveFormTab: (tabId: string) => void;
    handleOpenFormContainer: (tabId?: string) => void;
    setIsSearchOpen: (isSearchOpen: boolean) => void;
  };
}

const SearchFormContext = createContext<SearchFormContextProps | undefined>(
  undefined,
);

export const SearchFormProvider = ({ children }: { children: ReactNode }) => {
  const [openingSearchForms, setOpeningSearchForms] = useState(false);
  const {
    config: { searchTabs },
  } = useAppConstantsContext();

  const useSearchForm = (initialActiveTab = false) => {
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [activeFormTab, setActiveFormTab] = useState<string>(
      initialActiveTab ? searchTabs[0].id : "",
    );

    const handleTabChange = useCallback(
      (_event: React.SyntheticEvent, newValue: string) => {
        setActiveFormTab(newValue);
      },
      [],
    );

    useEffect(() => {
      if (!isSearchOpen) return;

      setOpeningSearchForms(true);

      setTimeout(() => {
        setOpeningSearchForms(false);
      }, 500);
    }, [isSearchOpen]);

    const handleOpenFormContainer = (tabId?: string) => {
      const newIsSearchOpen = !isSearchOpen;
      const newTabId = tabId || "";
      setIsSearchOpen(newIsSearchOpen);
      setActiveFormTab(newIsSearchOpen ? searchTabs[0].id : newTabId);
    };

    return {
      openingSearchForms,
      isSearchOpen,
      activeFormTab,
      searchTabs,
      handleTabChange,
      setActiveFormTab,
      handleOpenFormContainer,
      setIsSearchOpen,
    };
  };

  return (
    <SearchFormContext.Provider value={{ useSearchForm }}>
      {children}
    </SearchFormContext.Provider>
  );
};

export const useSearchFormContext = () => {
  const context = useContext(SearchFormContext);
  if (!context) {
    throw new Error(
      "useSearchFormContext must be used within a SearchFormProvider",
    );
  }
  return context;
};
